import React from "react";
// Change #100257 - "this is so hard to read - why?". RichTextRenderer.css is
// not imported anywhere, so its .rt-plain { white-space: pre-wrap } never
// applied and a plain-text note (API notes, pasted text) lost every line break -
// paragraphs, bullets and SQL all ran into one block. Importing that sheet now
// would switch on its other rules app-wide (a fixed text colour that breaks dark
// mode, font sizes) wherever notes render, so only the line breaks are applied,
// inline, on the plain-text output.
const PLAIN_TEXT_STYLE = { whiteSpace: "pre-wrap", wordBreak: "break-word" };

/* ---------------- helpers ---------------- */

const isString = (v) => typeof v === "string";

const safeParse = (value) => {
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
};

const tryParseDeep = (value, maxDepth = 5) => {
  let current = value;
  for (let i = 0; i < maxDepth; i++) {
    if (!isString(current)) return current;
    const trimmed = current.trim();
    const parsed = safeParse(trimmed);
    if (parsed === null) return current;
    current = parsed;
  }
  return current;
};

const decodeDescriptionValue = (value) => {
  if (!value) return { type: "empty", text: "", html: "", lexical: null };

  let current = value;
  if (isString(current)) current = tryParseDeep(current);

  if (current?.root)
    return { type: "lexical", lexical: current, text: "", html: "" };

  if (current?.html)
    return {
      type: "html",
      html: current.html,
      text: "",
      lexical: current?.json || null,
    };

  if (current?.json) {
    const inner = tryParseDeep(current.json);
    if (inner?.root)
      return { type: "lexical", lexical: inner, text: "", html: "" };
    if (inner?.html)
      return {
        type: "html",
        html: inner.html,
        text: "",
        lexical: inner?.json || null,
      };
    if (isString(inner))
      return { type: "text", text: inner, html: "", lexical: null };
  }

  if (isString(value) && /<\/?[a-z][\s\S]*>/i.test(value)) {
    return { type: "html", html: value, text: "", lexical: null };
  }

  return {
    type: "text",
    text: isString(value) ? value : JSON.stringify(value),
    html: "",
    lexical: null,
  };
};

const getFormatStyle = (format) => {
  const style = {};
  if (typeof format === "number") {
    if (format & 1) style.fontWeight = 700;
    if (format & 2) style.fontStyle = "italic";
    if (format & 4) style.textDecoration = "line-through";
    if (format & 8)
      style.textDecoration = style.textDecoration
        ? `${style.textDecoration} underline`
        : "underline";
    if (format & 16) style.verticalAlign = "sub";
    if (format & 32) style.verticalAlign = "super";
    if (format & 64) style.background = "#fff3bf";
    if (format & 128) style.fontFamily = "monospace";
  }
  return style;
};

/* --- NEW: Smart Lexical Tree Truncator --- */
const truncateLexicalTree = (lexicalRoot, limit) => {
  let charCount = 0;
  let limitReached = false;

  const walk = (node) => {
    // If we've hit the limit, stop processing further branches
    if (limitReached || !node) return null;

    // Clone the node so we don't mutate the original data
    const cloned = { ...node };

    if (cloned.type === "text" || cloned.type === "code-highlight") {
      const text = cloned.text || "";
      if (charCount + text.length >= limit) {
        // We reached the limit inside this text node. Slice it and add ellipsis.
        const allowed = limit - charCount;
        cloned.text = text.slice(0, allowed) + "…";
        limitReached = true;
      }
      charCount += text.length;
    } else if (cloned.children) {
      const newChildren = [];
      for (const child of cloned.children) {
        if (limitReached) break; // Drop remaining sibling nodes
        const newChild = walk(child);
        if (newChild) newChildren.push(newChild);
      }
      cloned.children = newChildren;
    }
    return cloned;
  };

  return walk(lexicalRoot);
};

/* ---------------- lexical renderers ---------------- */

const renderLeaf = (node, key) => {
  if (!node) return null;

  if (node.type === "linebreak") return <br key={key} />;
  if (node.type === "text")
    return (
      <span key={key} style={getFormatStyle(node.format)}>
        {node.text}
      </span>
    );
  if (node.type === "code-highlight") {
    const className = node.highlightType
      ? `rt-token rt-token-${node.highlightType}`
      : "rt-token";
    return (
      <span key={key} className={className}>
        {node.text}
      </span>
    );
  }
  if (node.type === "link") {
    return (
      <a
        key={key}
        href={node.url}
        target="_blank"
        rel="noopener noreferrer"
        className="rt-link"
      >
        {(node.children || []).map((child, i) =>
          renderLeaf(child, `${key}-${i}`)
        )}
      </a>
    );
  }
  if (node.children?.length) {
    return (
      <React.Fragment key={key}>
        {node.children.map((child, i) => renderLeaf(child, `${key}-${i}`))}
      </React.Fragment>
    );
  }
  return null;
};

const renderBlock = (node, key) => {
  if (!node) return null;
  const children = node.children || [];

  switch (node.type) {
    case "paragraph":
      return (
        <p key={key} className="rt-p">
          {children.length ? (
            children.map((child, i) => renderLeaf(child, `${key}-${i}`))
          ) : (
            <br />
          )}
        </p>
      );
    case "heading": {
      const Tag = node.tag || "h2";
      return (
        <Tag key={key} className={`rt-heading rt-${Tag}`}>
          {children.map((child, i) => renderLeaf(child, `${key}-${i}`))}
        </Tag>
      );
    }
    case "quote":
      return (
        <blockquote key={key} className="rt-quote">
          {children.map((child, i) => renderLeaf(child, `${key}-${i}`))}
        </blockquote>
      );
    case "list": {
      const Tag = node.listType === "number" ? "ol" : "ul";
      return (
        <Tag key={key} className="rt-list">
          {children.map((child, i) => renderBlock(child, `${key}-${i}`))}
        </Tag>
      );
    }
    case "listitem":
      return (
        <li key={key} className="rt-list-item">
          {children.map((child, i) =>
            child.type === "paragraph"
              ? child.children?.map((leaf, j) =>
                  renderLeaf(leaf, `${key}-${i}-${j}`)
                )
              : renderBlock(child, `${key}-${i}`)
          )}
        </li>
      );
    case "code": {
      // In a preview, you might not want massive code blocks. The tree truncator handles the length automatically.
      return (
        <div key={key} className="rt-code-wrap">
          {node.language ? (
            <div className="rt-code-lang">{node.language}</div>
          ) : null}
          <pre className="rt-code-block">
            <code>
              {children.map((child, i) => renderLeaf(child, `${key}-${i}`))}
            </code>
          </pre>
        </div>
      );
    }
    default:
      if (children.length) {
        return (
          <div key={key} className="rt-block">
            {children.map((child, i) =>
              child.children
                ? renderBlock(child, `${key}-${i}`)
                : renderLeaf(child, `${key}-${i}`)
            )}
          </div>
        );
      }
      return null;
  }
};

const renderLexicalState = (lexical) => {
  const rootChildren = lexical?.root?.children || [];
  return rootChildren.map((node, i) => renderBlock(node, `block-${i}`));
};

/* ---------------- main component ---------------- */

const RichTextRenderer = ({
  value,
  preview = false,
  previewLength = 220,
  clampLines = 4,
  className = "",
  emptyText = "—",
}) => {
  const decoded = decodeDescriptionValue(value);

  if (decoded.type === "empty") {
    return <span className={`rt-empty ${className}`}>{emptyText}</span>;
  }

  // PLAIN TEXT: Safe to slice string
  if (decoded.type === "text") {
    const text = decoded.text || "";
    const previewText =
      preview && text.length > previewLength
        ? `${text.slice(0, previewLength).trim()}…`
        : text;
    return (
      <div className={`rt-content rt-plain ${className}`} style={PLAIN_TEXT_STYLE}>
        {previewText || emptyText}
      </div>
    );
  }

  // LEXICAL: Use our Smart Tree Truncator
  if (decoded.type === "lexical") {
    let finalLexical = decoded.lexical;

    if (preview) {
      // Instead of flattening to text, we prune the JSON tree down to the character limit!
      finalLexical = {
        root: truncateLexicalTree(decoded.lexical?.root, previewLength),
      };
    }

    return (
      <div
        className={`rt-content ${
          preview ? "rt-preview-html-clamp" : ""
        } ${className}`}
        style={preview ? { "--rt-clamp-lines": clampLines } : undefined}
      >
        {renderLexicalState(finalLexical)}
      </div>
    );
  }

  // HTML: Do NOT slice strings here. Use a CSS class to visually clamp it so tags don't break.
  // HTML: Safely handle previews by stripping tags, otherwise render full HTML
  if (decoded.type === "html") {
    if (preview) {
      // 1. Strip HTML tags safely to get pure text (prevents layout breaks)
      const tempDiv = document.createElement("div");
      tempDiv.innerHTML = decoded.html || emptyText;
      const plainText = tempDiv.textContent || tempDiv.innerText || "";

      // 2. Enforce your character limit (e.g., 100)
      const previewText =
        plainText.length > previewLength
          ? `${plainText.slice(0, previewLength).trim()}…`
          : plainText;

      return (
        <div
          className={`rt-content rt-plain rt-preview-html-clamp ${className}`}
          style={{ "--rt-clamp-lines": clampLines }}
        >
          {previewText}
        </div>
      );
    }

    // Standard Full HTML Render (When not in preview mode)
    return (
      <div
        className={`rt-content ${className}`}
        dangerouslySetInnerHTML={{
          __html: decoded.html || `<div>${emptyText}</div>`,
        }}
      />
    );
  }

  return <div className={`rt-content rt-plain ${className}`}>{emptyText}</div>;
};

export default RichTextRenderer;
